import React, { useEffect, useRef, useState } from "react";
import { api } from "../../../software/api";
import { toast } from "sonner";
import { printPettyCashDocket, printPettyCashBackDocket } from "../reports/legacy/docs";

// Replicas of Form_POSCashDrawer (petty cash out) and Form_POSCashDrawerModify (change back). Twips / 17.
const T = (l, t, w, h) => ({ position: "absolute", left: l / 17, top: t / 17, width: w / 17, height: h / 17 });
const PRESETS = [["Milk", 13719, 8844], ["Aus Post", 15420, 8844], ["Food", 13719, 9751], ["Bunnings", 15420, 9751], ["Personal", 13719, 10658], ["Office Works", 15420, 10658]];
const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const lbl = "text-white font-black drop-shadow text-center";

function Frame({ title, testid, onClose, children }) {
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm overflow-auto" data-testid={`${testid}-overlay`}>
      <div className="relative shrink-0 rounded-lg overflow-hidden shadow-2xl border border-black/40" data-testid={testid}
        style={{ width: 1267, height: 700, backgroundImage: "url('/cash-drawer-bg.png')", backgroundSize: "cover", backgroundPosition: "center" }}>
        <div className="absolute inset-0 bg-black/45" />
        <div style={T(0, 113, 21542, 900)} className={`${lbl} text-[38px] underline flex items-center justify-center`} data-testid={`${testid}-title`}>{title}</div>
        <button onClick={onClose} data-testid={`${testid}-close`} className="absolute right-3 bottom-3 text-white/80 hover:text-white text-sm font-bold border border-white/40 rounded px-3 py-1">Close</button>
        {children}
      </div>
    </div>
  );
}

export function PettyCashOut({ clerk, company, onDone, onClose }) {
  const [amt, setAmt] = useState("");
  const [step, setStep] = useState(1);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const reasonRef = useRef(null);
  const toReason = () => { if (!(parseFloat(amt) > 0)) return toast.error("Enter how much petty cash you are taking."); setStep(2); setTimeout(() => reasonRef.current?.focus(), 30); };
  const save = async (r) => {
    const why = (r ?? reason).trim();
    if (!why) return toast.error("Select a preset or type what the petty cash is for.");
    setBusy(true);
    try {
      const e = await api("POST", "/pos/petty-cash", { amount: parseFloat(amt), reason: why, taken_by: clerk });
      printPettyCashDocket(e.amount, e.reason, e.taken_by, company);
      try { await api("POST", "/pos/open-drawer"); } catch { /* non-fatal */ }
      toast.success(`Petty cash ${money(e.amount)} recorded`); onDone();
    } catch (err) { toast.error(err.message); setBusy(false); }
  };
  return (
    <Frame title="CASH DRAWER PETTY CASH TAKINGS" testid="petty-out" onClose={onClose}>
      <div style={T(6280, 4251, 9000, 480)} className={`${lbl} text-[21px]`}>HOW MUCH PETTY CASH ARE YOU TAKING $ ?</div>
      <input style={T(7937, 4761, 5669, 690)} value={amt} autoFocus inputMode="decimal" placeholder="0.00" data-testid="petty-out-amount"
        onChange={(e) => setAmt(e.target.value.replace(/[^0-9.]/g, ""))} onKeyDown={(e) => { if (e.key === "Enter") toReason(); }}
        className="bg-[#fff7b0] border border-black/60 text-[30px] font-black text-center outline-none focus:ring-2 focus:ring-amber-400" />
      {step === 1 && <button style={T(8937, 5700, 3669, 600)} onClick={toReason} data-testid="petty-out-next" className="rounded-md bg-sky-700 hover:bg-sky-600 text-white font-black shadow-lg">NEXT</button>}
      {step === 2 && <>
        <div style={T(680, 10044, 5669, 1100)} className="text-white font-black text-[18px] drop-shadow" data-testid="petty-out-takenby">
          <div>{clerk}</div><div>{new Date().toLocaleDateString("en-AU", { timeZone: "Australia/Sydney" })}</div>
        </div>
        <div style={T(2948, 8844, 4784, 1470)} className={`${lbl} text-[18px] leading-snug`}>Select from presets or simply type in what you require the petty cash for?</div>
        <div style={T(10100, 8290, 1335, 480)} className={`${lbl} text-[20px]`}>REASON</div>
        <textarea ref={reasonRef} style={T(7937, 8844, 5669, 1365)} value={reason} data-testid="petty-out-reason"
          onChange={(e) => setReason(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); save(); } }}
          className="bg-[#fff7b0] border border-black/60 text-[20px] font-bold px-2 outline-none resize-none focus:ring-2 focus:ring-amber-400" />
        {PRESETS.map(([p, l, t]) => (
          <button key={p} style={T(l, t, 1575, 796)} disabled={busy} onClick={() => { setReason(p); save(p); }} data-testid={`petty-out-preset-${p.toLowerCase().replace(/\s/g, "-")}`}
            className="rounded bg-[#dca88f] hover:bg-[#e8bda8] text-[#0000ff] font-bold text-[17px] border border-black/40 shadow">{p}</button>
        ))}
        <button style={T(7937, 10431, 5669, 690)} disabled={busy} onClick={() => save()} data-testid="petty-out-save"
          className="rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-black text-[18px] shadow-lg">{busy ? "Saving…" : "PRINT DOCKET & OPEN TILL"}</button>
      </>}
    </Frame>
  );
}

export function PettyCashBack({ company, onDone, onClose }) {
  const [rows, setRows] = useState(null);
  const [edit, setEdit] = useState({});
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api("GET", "/pos/petty-cash/today").then((r) => {
      const mine = r.filter((x) => /^[0-9a-f-]{36}$/.test(String(x.PettyCashID)));
      if (!mine.length) { toast.info("No money has been recorded as having been taken out of Petty Cash."); onClose(); return; }
      setRows(mine); setEdit(Object.fromEntries(mine.map((x) => [x.PettyCashID, x.PettyCashAmount.toFixed(2)])));
    }).catch((e) => { toast.error(e.message); onClose(); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const finish = async () => {
    setBusy(true);
    try {
      await api("PATCH", `/pos/petty-cash/${picked}`, { amount: parseFloat(edit[picked]) || 0 });
      printPettyCashBackDocket(company);
      try { await api("POST", "/pos/open-drawer"); } catch { /* non-fatal */ }
      toast.success("Petty cash updated to the receipt amount"); onDone();
    } catch (e) { toast.error(e.message); setBusy(false); }
  };
  if (!rows) return null;
  return (
    <Frame title="PETTY CASH CHANGE BACK TO CASH DRAWER" testid="petty-back" onClose={onClose}>
      {!picked ? <>
        <div style={T(5669, 3058, 10200, 960)} className={`${lbl} text-[21px] leading-snug`}>Alter Petty Cash Amount Taken to Receipt Amount then place receipt and change back into Cash Drawer</div>
        <div style={{ ...T(6239, 4435, 9225, 4000), height: "auto" }} className="bg-white/95 border border-black/40 divide-y divide-slate-300" data-testid="petty-back-list">
          {rows.map((r) => (
            <div key={r.PettyCashID} className="grid grid-cols-[30%_18%_1fr] items-center text-[15px]" data-testid="petty-back-row">
              <span className="px-2 truncate">{r.PettyCashTakenBy}</span>
              <input value={edit[r.PettyCashID]} inputMode="decimal" data-testid={`petty-back-amount-${r.PettyCashID}`}
                onChange={(e) => setEdit({ ...edit, [r.PettyCashID]: e.target.value.replace(/[^0-9.]/g, "") })}
                onKeyDown={(e) => { if (e.key === "Enter") setPicked(r.PettyCashID); }}
                className="bg-[#ffcc66] text-right px-2 py-1 font-bold outline-none focus:ring-2 focus:ring-amber-500" />
              <span className="px-2 truncate flex items-center justify-between gap-2">{r.PettyCashReason}
                <button onClick={() => setPicked(r.PettyCashID)} data-testid={`petty-back-pick-${r.PettyCashID}`} className="text-[12px] font-bold text-sky-700 hover:underline shrink-0">Update</button></span>
            </div>
          ))}
        </div>
      </> : <>
        <div style={T(1407, 5548, 18685, 900)} className={`${lbl} text-[32px] whitespace-nowrap`} data-testid="petty-back-place">Place receipt and change back into Cash Drawer</div>
        <div style={T(5669, 7500, 10200, 600)} className={`${lbl} text-[22px]`}>Receipt amount: {money(edit[picked])}</div>
        <button style={T(7937, 8674, 5669, 800)} disabled={busy} onClick={finish} data-testid="petty-back-save"
          className="rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-black text-[18px] shadow-lg">{busy ? "Saving…" : "PRINT DOCKET & FINISH"}</button>
        <button style={T(7937, 9700, 5669, 500)} onClick={() => setPicked(null)} data-testid="petty-back-back" className="text-white/90 underline font-bold">Back</button>
      </>}
    </Frame>
  );
}
